import axios from 'axios';
import EventBus from 'eventing-bus';
import { all, takeEvery, call, put } from 'redux-saga/effects';

// Pricing knobs + workspace mailbox capacity
function* getZapmailPlan() {
  const { error, response } = yield call(getCall, '/zapmail/plan');
  if (error) return; // feature disabled — Domains tab shows its empty state
  else if (response) yield put({ type: 'SET_ZAPMAIL_PLAN', payload: response['data']['body'] });
}

// Domain availability search — results returned via callback, not the store
function* searchZapmailDomains({ payload }) {
  const { data, successCallback, errorCallback } = payload;
  const { error, response } = yield call(postCall, { path: '/zapmail/search', payload: data });
  if (error) {
    EventBus.publish('error', error['response']['data']['message']);
    if (errorCallback) errorCallback(error);
  } else if (response && successCallback) successCallback(response['data']['body']);
}

// Load more suggested domains — same callback contract as the search
function* loadMoreZapmailDomains({ payload }) {
  const { data, successCallback, errorCallback } = payload;
  const { error, response } = yield call(postCall, { path: '/zapmail/search/more', payload: data });
  if (error) {
    EventBus.publish('error', error['response']['data']['message']);
    if (errorCallback) errorCallback(error);
  } else if (response && successCallback) successCallback(response['data']['body']);
}

// Create order + redirect to Stripe hosted checkout — or, for admin skip-payment orders, straight
// to the Domains tab success view (mimics the Stripe return so the provisioning card just works)
function* createZapmailCheckout({ payload }) {
  const { data, errorCallback } = payload;
  const { error, response } = yield call(postCall, { path: '/zapmail/orders/checkout', payload: data });
  if (error) {
    EventBus.publish('error', error['response']['data']['message']);
    if (errorCallback) errorCallback(error);
  } else if (response && response['data']['body']['url']) window.location.href = response['data']['body']['url'];
  else if (response && response['data']['body']['skipped']) window.location.href = `/settings?tab=integrations-domains&order=${response['data']['body']['orderId']}&status=success`;
}

// List orders
function* getZapmailOrders() {
  const { error, response } = yield call(getCall, '/zapmail/orders');
  if (error) return EventBus.publish('error', error['response']['data']['message']);
  else if (response) yield put({ type: 'SET_ZAPMAIL_ORDERS', payload: response['data']['body'] });
}

// Single order detail (provisioning card poll)
function* getZapmailOrder({ payload }) {
  const { error, response } = yield call(getCall, `/zapmail/orders/${payload['orderId']}`);
  if (error) return EventBus.publish('error', error['response']['data']['message']);
  else if (response) yield put({ type: 'SET_ZAPMAIL_ACTIVE_ORDER', payload: response['data']['body'] });
}

// Retry provisioning
function* retryZapmailOrder({ payload }) {
  const { orderId, successCallback } = payload;
  const { error, response } = yield call(postCall, { path: `/zapmail/orders/${orderId}/retry`, payload: {} });
  if (error) return EventBus.publish('error', error['response']['data']['message']);
  else if (response) {
    EventBus.publish('success', 'Provisioning retry started');
    if (successCallback) successCallback();
  }
}

// Cancel all of this user's unpaid orders — silent (used when superseding an abandoned checkout)
function* cancelPendingZapmailOrders({ payload }) {
  const { successCallback } = payload;
  const { response } = yield call(postCall, { path: '/zapmail/orders/cancel-pending', payload: {} });
  if (response && successCallback) successCallback();
}

// List purchased domains
function* getZapmailDomains() {
  const { error, response } = yield call(getCall, '/zapmail/domains');
  if (error) return EventBus.publish('error', error['response']['data']['message']);
  else if (response) yield put({ type: 'SET_ZAPMAIL_DOMAINS', payload: response['data']['body'] });
}

// Renewal checkout — redirects to Stripe
function* renewZapmailDomain({ payload }) {
  const { domainId, errorCallback } = payload;
  const { error, response } = yield call(postCall, { path: `/zapmail/domains/${domainId}/renew`, payload: {} });
  if (error) {
    EventBus.publish('error', error['response']['data']['message']);
    if (errorCallback) errorCallback(error);
  } else if (response && response['data']['body']['url']) window.location.href = response['data']['body']['url'];
}

// Save wizard draft — silent background save; a failed save must never interrupt the purchase flow
function* saveZapmailDraft({ payload }) {
  const { data, successCallback } = payload;
  const { response } = yield call(putCall, { path: '/zapmail/draft', payload: data });
  if (response && successCallback) successCallback(response['data']['body']);
}

// Discard the shared wizard draft
function* deleteZapmailDraft({ payload }) {
  const { response } = yield call(deleteCall, '/zapmail/draft');
  if (response && payload?.['successCallback']) payload['successCallback']();
}

// Admin skip-payment cost preview — silent (non-admins simply never see the data)
function* getZapmailAdminCostPreview({ payload }) {
  const { data, successCallback } = payload;
  const { response } = yield call(postCall, { path: '/zapmail/admin/cost-preview', payload: data });
  if (response && successCallback) successCallback(response['data']['body']);
}

// Mailbox credential reveal — callback only, the plaintext never touches the store
function* getZapmailMailboxCredentials({ payload }) {
  const { integrationId, successCallback, errorCallback } = payload;
  const { error, response } = yield call(getCall, `/zapmail/mailboxes/${integrationId}/credentials`);
  if (error) {
    EventBus.publish('error', error['response']['data']['message']);
    if (errorCallback) errorCallback(error);
  } else if (response && successCallback) successCallback(response['data']['body']);
}

// Update mailbox identity (multipart — axios sets the content type from the FormData)
function* updateZapmailMailbox({ payload }) {
  const { integrationId, formData, successCallback, errorCallback } = payload;
  const { error, response } = yield call(putCall, { path: `/zapmail/mailboxes/${integrationId}`, payload: formData });
  if (error) {
    EventBus.publish('error', error['response']['data']['message']);
    if (errorCallback) errorCallback(error);
  } else if (response) {
    EventBus.publish('success', 'Mailbox updated');
    if (successCallback) successCallback(response['data']['body']);
  }
}

// Watcher saga for zapmail (Done-For-You email infrastructure) actions
function* actionWatcher() {
  yield takeEvery('GET_ZAPMAIL_PLAN', getZapmailPlan);
  yield takeEvery('SEARCH_ZAPMAIL_DOMAINS', searchZapmailDomains);
  yield takeEvery('LOAD_MORE_ZAPMAIL_DOMAINS', loadMoreZapmailDomains);
  yield takeEvery('CREATE_ZAPMAIL_CHECKOUT', createZapmailCheckout);
  yield takeEvery('GET_ZAPMAIL_ORDERS', getZapmailOrders);
  yield takeEvery('GET_ZAPMAIL_ORDER', getZapmailOrder);
  yield takeEvery('RETRY_ZAPMAIL_ORDER', retryZapmailOrder);
  yield takeEvery('CANCEL_PENDING_ZAPMAIL_ORDERS', cancelPendingZapmailOrders);
  yield takeEvery('GET_ZAPMAIL_DOMAINS', getZapmailDomains);
  yield takeEvery('RENEW_ZAPMAIL_DOMAIN', renewZapmailDomain);
  yield takeEvery('GET_ZAPMAIL_ADMIN_COST_PREVIEW', getZapmailAdminCostPreview);
  yield takeEvery('GET_ZAPMAIL_MAILBOX_CREDENTIALS', getZapmailMailboxCredentials);
  yield takeEvery('UPDATE_ZAPMAIL_MAILBOX', updateZapmailMailbox);
  yield takeEvery('SAVE_ZAPMAIL_DRAFT', saveZapmailDraft);
  yield takeEvery('DELETE_ZAPMAIL_DRAFT', deleteZapmailDraft);
}

export default function* rootSaga() {
  yield all([actionWatcher()]);
}

// Helper function to make POST requests to the server
function postCall({ path, payload }) {
  return axios
    .post(path, payload)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make DELETE requests to the server
function deleteCall(path) {
  return axios
    .delete(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make PUT requests to the server
function putCall({ path, payload }) {
  return axios
    .put(path, payload)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make GET requests to the server
function getCall(path) {
  return axios
    .get(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}
